import React, { useEffect } from 'react';
import Lenis from 'lenis';
import './App.css';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Toaster } from './components/ui/toaster';
import Navigation from './components/Navigation';
import ScrollVideoHero from './components/ScrollVideoHero';
import TrustBar from './components/TrustBar';
import ReviewsMarquee from './components/ReviewsMarquee';
import { DOCK_LIFT_REVIEWS, BARGE_REVIEWS } from './data/reviews';
import useGoogleReviews from './hooks/useGoogleReviews';
import WhyHDPE from './components/WhyHDPE';
import OptionsSpecs from './components/OptionsSpecs';
import PneumaticHammer from './components/PneumaticHammer';
import WhyChooseUs, { BargeDivider } from './components/WhyChooseUs';
import ServiceArea from './components/ServiceArea';
import Financing from './components/Financing';
import FAQ from './components/FAQ';
import Gallery from './components/Gallery';
import Contact from './components/Contact';
import CostCalculator from './components/CostCalculator';
import Footer from './components/Footer';
import PrivacyPolicy from './components/PrivacyPolicy';
import SmsTerms from './components/SmsTerms';
import AbandonedEstimateNudge from './components/AbandonedEstimateNudge';
import HammerLanding from './pages/HammerLanding';
import BuildMyBarge from './pages/BuildMyBarge';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import useSeo from './hooks/useSeo';

const HOME_TITLE = 'HDPE Work Barges for Sale | Absolute Barges - Fort Myers, FL';
const HOME_DESC =
  "Custom HDPE work barges for sale — 8'×24' & 10'×30' work barges built in Fort Myers, FL for marine construction. Financing available. Delivered to the Lower 48.";
const HOME_CANONICAL = 'https://absolutebarges.com/';

// When the site is served from absolutehammers.com we swap the root route
// to render the hammer landing page instead of the barge home. The preview
// host used to be in this list while we were building the hammer site;
// it's been removed so the preview now defaults back to the barge home.
// Barge home stays reachable at `/barges` regardless of hostname, and
// `/hammer` continues to work everywhere.
const HAMMER_HOSTS = [
  'absolutehammers.com',
  'www.absolutehammers.com',
];
const isHammerHost =
  typeof window !== 'undefined' && HAMMER_HOSTS.includes(window.location.hostname);

function Home() {
  useSeo({ title: HOME_TITLE, description: HOME_DESC, canonical: HOME_CANONICAL });
  const g = useGoogleReviews();
  const live = g && g.source === 'google' && Array.isArray(g.reviews) && g.reviews.length > 0;
  const bargeReviews = live ? g.reviews : BARGE_REVIEWS;
  const bargeTitle = live
    ? "Live Google reviews \u2014 shown in Google's relevance order"
    : 'What our HDPE barge & hammer customers say';
  return (
    <div className="App bg-black min-h-screen">
      <Navigation />
      <main>
        <ScrollVideoHero />
        <TrustBar />
        <WhyHDPE />
        <CostCalculator />
        <OptionsSpecs />
        <PneumaticHammer />
        <WhyChooseUs />
        <ReviewsMarquee
          testId="reviews-dock-lift"
          title="What our dock & boat lift customers say"
          reviews={DOCK_LIFT_REVIEWS}
          direction="ltr"
        />
        <BargeDivider />
        <ServiceArea />
        <Financing />
        <FAQ />
        <Gallery />
        <ReviewsMarquee
          testId="reviews-barge"
          title={bargeTitle}
          reviews={bargeReviews}
          direction="rtl"
        />
        <Contact />
      </main>
      <Footer />
      <AbandonedEstimateNudge />
      <Toaster />
    </div>
  );
}

function App() {
  // Lenis: smooth momentum scrolling with native anchor support.
  useEffect(() => {
    const lenis = new Lenis({ autoRaf: true, anchors: true });
    return () => lenis.destroy();
  }, []);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={isHammerHost ? <HammerLanding /> : <Home />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/sms-terms" element={<SmsTerms />} />
        <Route path="/hammer" element={<HammerLanding />} />
        <Route path="/barges" element={<Home />} />
        <Route path="/build" element={<BuildMyBarge />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminDashboard />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
